import React from 'react';

/**
 * Status glyphs (Oct 2026, trader theme). 14px, 1.5px strokes on a shared grey
 * track; colour only when the state needs attention. One glyph per question:
 *  - RuleGlyph: how close am I to my own lines? (cyan when a rule fired)
 *  - StrikeGlyph: where is the stock vs my strike? (amber within 2%, red ITM)
 *  - PremiumGlyph: how much of the premium is mine? (green past my profit line)
 * Inputs are API values; pass null when unknown and the glyph draws its empty state.
 */

const TRACK = 'rgba(255, 255, 255, 0.16)';
// Theme values as literals: SVG presentation attributes don't resolve var() reliably.
const GREY = '#a2a7ae';
const SIG = '#7fd0ea';
const WARN = '#e8b85a';
const LOSS = '#e08a7e';
const GAIN = '#5cc79a';
const NEAR = 0.8;

interface RuleProps {
  /** 0–1 toward the nearest unfired line; null when unknown. */
  progress: number | null;
  fired: boolean;
  title?: string;
}

export const RuleGlyph: React.FC<RuleProps> = ({ progress, fired, title }) => {
  if (fired) {
    return (
      <svg className="glyph" width="14" height="14" viewBox="0 0 14 14" role="img" aria-label={title ?? 'A rule fired'}>
        {title && <title>{title}</title>}
        <circle cx="7" cy="7" r="6" fill={SIG} />
        <path d="M4.4 7.2l1.8 1.8 3.4-3.6" stroke="#0b0c0e" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  const r = 5.5;
  const c = 2 * Math.PI * r;
  const f = progress == null ? 0 : Math.max(0, Math.min(1, progress));
  const label = title ?? (progress == null ? 'No rule fired' : `${Math.round(f * 100)}% of the way to your nearest line`);
  return (
    <svg className="glyph" width="14" height="14" viewBox="0 0 14 14" role="img" aria-label={label}>
      <title>{label}</title>
      <circle cx="7" cy="7" r={r} fill="none" stroke={TRACK} strokeWidth="1.5" />
      {f > 0 && (
        <circle
          cx="7" cy="7" r={r} fill="none" stroke={f >= NEAR ? WARN : GREY} strokeWidth="1.5"
          strokeDasharray={`${(c * f).toFixed(2)} ${c.toFixed(2)}`} transform="rotate(-90 7 7)"
        />
      )}
    </svg>
  );
};

interface StrikeProps {
  /** Distance to strike as % of price; positive = out of the money, negative = in the money. */
  otmPct: number | null;
}

export const StrikeGlyph: React.FC<StrikeProps> = ({ otmPct }) => {
  const label = otmPct == null ? 'Distance to strike unknown'
    : otmPct >= 0 ? `${otmPct.toFixed(1)}% out of the money` : `${Math.abs(otmPct).toFixed(1)}% in the money`;
  // Track spans ±10% around the strike tick; the dot is the stock, left of the strike when OTM.
  const x = otmPct == null ? null : 15 - Math.max(-10, Math.min(10, otmPct)) * 1.2;
  const col = otmPct == null ? GREY : otmPct < 0 ? LOSS : otmPct < 2 ? WARN : GREY;
  return (
    <svg className="glyph glyph-wide" width="30" height="14" viewBox="0 0 30 14" role="img" aria-label={label}>
      <title>{label}</title>
      <line x1="2" y1="7" x2="28" y2="7" stroke={TRACK} strokeWidth="1.5" />
      <line x1="15" y1="2.5" x2="15" y2="11.5" stroke={GREY} strokeWidth="1.5" />
      {x != null && <circle cx={x.toFixed(1)} cy="7" r="2.6" fill={col} />}
    </svg>
  );
};

interface PremiumProps {
  /** Share of the premium captured, 0–1; null when unknown. */
  captured: number | null;
  /** Your profit target as 0–1 (drawn as a hairline); null to omit. */
  line: number | null;
}

export const PremiumGlyph: React.FC<PremiumProps> = ({ captured, line }) => {
  const f = captured == null ? 0 : Math.max(0, Math.min(1, captured));
  const past = captured != null && line != null && f >= line;
  const col = past ? GAIN : GREY;
  const a = f * 2 * Math.PI;
  const x = 7 + 6 * Math.sin(a);
  const y = 7 - 6 * Math.cos(a);
  const label = captured == null ? 'Premium captured unknown'
    : `${Math.round(f * 100)}% of the premium captured${line != null ? `; your profit line is ${Math.round(line * 100)}%` : ''}`;
  return (
    <svg className="glyph" width="14" height="14" viewBox="0 0 14 14" role="img" aria-label={label}>
      <title>{label}</title>
      <circle cx="7" cy="7" r="6" fill="none" stroke={TRACK} strokeWidth="1.5" />
      {f >= 0.999 ? <circle cx="7" cy="7" r="6" fill={col} />
        : f > 0 && <path d={`M7 7 L7 1 A6 6 0 ${f > 0.5 ? 1 : 0} 1 ${x.toFixed(2)} ${y.toFixed(2)} Z`} fill={col} />}
      {line != null && (
        <line
          x1="7" y1="7"
          x2={(7 + 6.8 * Math.sin(line * 2 * Math.PI)).toFixed(2)} y2={(7 - 6.8 * Math.cos(line * 2 * Math.PI)).toFixed(2)}
          stroke="#e7e8ea" strokeWidth="1" opacity="0.55"
        />
      )}
    </svg>
  );
};

interface RangeProps {
  value: number | null;
  min: number;
  max: number;
  /** Band cut-offs (e.g. the setup terciles); the band the value falls in is drawn brighter. */
  cuts?: [number, number];
  /** A reference mark (e.g. 0 for a trend, 1 for an IV/HV ratio). */
  mark?: number;
  label: string;
}

/** Where a value sits in its range: grey track, optional bands or a reference
 *  mark, a dot at the value. 44×14; no colour, position carries the meaning. */
export const RangeGlyph: React.FC<RangeProps> = ({ value, min, max, cuts, mark, label }) => {
  const W = 44, L = 2, R = 42;
  const x = (v: number) => L + ((Math.max(min, Math.min(max, v)) - min) / (max - min)) * (R - L);
  const band = cuts && value != null ? (value <= cuts[0] ? 0 : value <= cuts[1] ? 1 : 2) : null;
  const edges = cuts ? [L, x(cuts[0]), x(cuts[1]), R] : null;
  return (
    <svg className="glyph glyph-range" width={W} height="14" viewBox={`0 0 ${W} 14`} role="img" aria-label={label}>
      <title>{label}</title>
      {edges ? (
        [0, 1, 2].map((i) => (
          <line key={i} x1={edges[i] + (i ? 1 : 0)} y1="7" x2={edges[i + 1] - (i < 2 ? 1 : 0)} y2="7"
            stroke={band === i ? 'rgba(255, 255, 255, 0.42)' : TRACK} strokeWidth={band === i ? 2 : 1.5} />
        ))
      ) : (
        <line x1={L} y1="7" x2={R} y2="7" stroke={TRACK} strokeWidth="1.5" />
      )}
      {mark != null && <line x1={x(mark)} y1="3" x2={x(mark)} y2="11" stroke={GREY} strokeWidth="1" opacity="0.7" />}
      {value != null && <circle cx={x(value).toFixed(1)} cy="7" r="2.6" fill="#e7e8ea" />}
    </svg>
  );
};
